// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { removeTypename } from '../../../common/utils'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import ImageOverlay from '../ImageOverlay/ImageOverlay'
import RequiresAction from '../RequiresAction'
import WeeklyInfoPage from '../WeeklyInfoPage'
import Images from '../components/Images'
import Links from '../components/Links'
import { fetchManagerPageScoreCardStats, fetchWeeklyInfo, fetchWeeklyInfoScoreCard } from '../weeklyInfoQueries'
import type { WeeklyInfoGeneral, WeeklyInfoVariables } from '../weeklyInfoTypes'
import css from './managerPage.css'
type WeeklyInfoGeneralTotal = {
  totalActualSales: number
  totalBudgetedSales: number
  totalPlannedHours: number
  totalPlannedSales: number
  totalActualHours: number
  actualEfficiency: number
  plannedEfficiency: number
  budgetedEfficiency: number
}

const calculateTotalData = (data: Array<WeeklyInfoGeneral>): WeeklyInfoGeneralTotal => {
  let totalActualSales = 0
  let totalPlannedSales = 0
  let totalPlannedHours = 0
  let totalBudgetedSales = 0
  let totalBudgetedHours = 0
  let totalActualHours = 0
  data.forEach((item) => {
    totalActualSales += item.totalActualSales
    totalPlannedSales += item.totalPlannedSales
    totalActualHours += item.totalActualHours
    totalPlannedHours += item.totalPlannedHours
    totalBudgetedHours += item.totalBudgetedHours
    totalBudgetedSales += item.totalBudgetedSales
  })
  return {
    totalActualSales: Math.round(totalActualSales),
    totalPlannedSales: Math.round(totalPlannedSales),
    totalActualHours: Math.round(totalActualHours),
    totalBudgetedSales: Math.round(totalBudgetedSales),
    totalPlannedHours: Math.round(totalPlannedHours),
    actualEfficiency: calculateEfficiency(totalActualSales, totalActualHours),
    budgetedEfficiency: calculateEfficiency(totalBudgetedSales, totalBudgetedHours),
    plannedEfficiency: calculateEfficiency(totalPlannedSales, totalPlannedHours),
  }
}

const calculateEfficiency = (actualSales: number, actualHours: number): number =>
  actualSales && actualHours ? Math.round(actualSales / actualHours) : 0

type SalesAndHoursRowProps = {
  stationId: string
  date: string
  data: WeeklyInfoGeneral
  canEdit: boolean
}

const SalesAndHoursRow = ({ stationId, date, data, canEdit }: SalesAndHoursRowProps) => {
  const actualSales = Math.round(data.totalActualSales) || 0
  const plannedSales = Math.round(data.totalPlannedSales) || 0
  const budgetedSales = Math.round(data.totalBudgetedSales) || 0
  const actualHours = Math.round(data.totalActualHours) || 0
  const plannedHours = Math.round(data.totalPlannedHours) || 0
  const actualEfficiency = calculateEfficiency(actualSales, actualHours)
  const budgetedEfficiency = Math.round(data.budgetedEfficiency) || 0
  const plannedEfficiency = Math.round(data.plannedEfficiency) || 0
  return (
    <TableRow>
      <TableCell align='center' className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`weeklyInfo.heading.profitCenter${data.profitCenterType}`)}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedSales} &euro;</strong>
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualSales >= plannedSales,
          [css.negativeCell]: actualSales < plannedSales,
        })}
      >
        {actualSales} &euro;
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedSales} &euro;
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualHours <= plannedHours,
          [css.negativeCell]: actualHours > plannedHours,
        })}
      >
        {actualHours} h
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedHours} h
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualEfficiency >= plannedEfficiency,
          [css.negativeCell]: actualEfficiency < plannedEfficiency,
        })}
      >
        {actualEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedEfficiency}</strong>
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            weeklyInfoId={data.id}
            subject='Management'
            isActionRequired={data.isActionRequired}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

type SalesAndHoursTotalRowProps = {
  stationId: string
  date: string
  data: Array<WeeklyInfoGeneral>
  canEdit: boolean
}

const SalesAndHoursTotalRow = ({ stationId, date, data, canEdit }: SalesAndHoursTotalRowProps) => {
  const {
    totalActualSales,
    totalPlannedSales,
    totalActualHours,
    totalPlannedHours,
    totalBudgetedSales,
    actualEfficiency,
    budgetedEfficiency,
    plannedEfficiency,
  } = calculateTotalData(data)
  return (
    <TableRow>
      <TableCell align='center' className={css.tableHeading}>
        {i18n.t('weeklyInfo.heading.total')}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{totalBudgetedSales} &euro;</strong>
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: totalActualSales >= totalPlannedSales,
          [css.negativeCell]: totalActualSales < totalPlannedSales,
        })}
      >
        {totalActualSales} &euro;
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {totalPlannedSales} &euro;
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: totalActualHours <= totalPlannedHours,
          [css.negativeCell]: totalActualHours > totalPlannedHours,
        })}
      >
        {totalActualHours} h
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {totalPlannedHours} h
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualEfficiency >= plannedEfficiency,
          [css.negativeCell]: actualEfficiency < plannedEfficiency,
        })}
      >
        {actualEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedEfficiency}</strong>
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            weeklyInfoId={data.id}
            subject='Management'
            isActionRequired={data.isActionRequired === undefined ? null : data.isActionRequired}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

type SalesAndHoursProps = {
  stationId: string
  date: string
}

const SalesAndHours = ({ stationId, date }: SalesAndHoursProps) => (
  <Query<Array<WeeklyInfoGeneral>, WeeklyInfoVariables>
    query={fetchWeeklyInfo}
    variables={{
      stationId,
      date,
      type: 'General',
    }}
  >
    {({ data, error, loading }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.salesAndHours}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('weeklyInfo.title.salesAndHours', {
              week: `vko. ${moment(date).isoWeek()} ${moment(date).startOf('week').format('D.M.')}-${moment(date)
                .endOf('week')
                .format('D.M.')}`,
            })}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table size='small' className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center' className={css.tableHeading} />
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.budgetedSales')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.actualSales')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.plannedSales')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.actualHours')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.plannedHours')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.actualEfficiency')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.plannedEfficiency')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.budgetedEfficiency')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {get(data, 'weeklyInfo', []).map((item) => (
                      <SalesAndHoursRow
                        stationId={stationId}
                        date={date}
                        data={item}
                        key={item.id}
                        canEdit={hasPermission}
                      />
                    ))}
                    <SalesAndHoursTotalRow
                      stationId={stationId}
                      date={date}
                      data={get(data, 'weeklyInfo', [])}
                      canEdit={hasPermission}
                    />
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

const ScoreCardTable = ({ stationId, date }) => {
  return (
    <Query
      query={fetchManagerPageScoreCardStats}
      variables={{
        stationId,
      }}
    >
      {({ loading, error, data }) => {
        if (loading || error) {
          return <CircularProgress />
        }

        return (
          <Paper className={css.scoreCard}>
            <Table className={css.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderName')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderRollingMonth')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderLastWeek')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderThisWeek')}</TableCell>
                  <TableCell className={cx(css.tableHeading, css.actionCell)}>
                    {i18n.t('weeklyInfo.heading.isActionRequired')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(removeTypename(data.scoreCardStats)).map((row: any, i: number) => {
                  const [category, values] = row
                  const { thisWeek, lastWeek, rollingMonth } = values || {}

                  return (
                    <TableRow key={i}>
                      <TableCell className={css.tableCell}>{i18n.t(`scoreCard.${category}`)}</TableCell>
                      <TableCell className={css.tableCell}>
                        <div className={css.cellValue}>
                          <span className={cx(css.colorCircle)} />
                          {rollingMonth.actual}
                        </div>
                      </TableCell>
                      <TableCell className={css.tableCell}>
                        <div className={css.cellValue}>
                          <span className={cx(css.colorCircle)} />
                          {lastWeek.actual}
                        </div>
                      </TableCell>
                      <TableCell className={css.tableCell}>
                        {values.thisWeek && (
                          <div className={css.cellValue}>
                            <span className={cx(css.colorCircle)} />
                            {thisWeek.actual}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={css.actionCell}>
                        <Query
                          query={fetchWeeklyInfoScoreCard}
                          variables={{
                            stationId,
                            date,
                            type: 'ScoreCard',
                            row: `manager_${category}`,
                          }}
                        >
                          {({ loading, error, data }) => {
                            if (loading || error) {
                              return <CircularProgress />
                            }

                            const info = get(data, 'weeklyInfo.0', {})
                            return (
                              <RequiresAction
                                stationId={stationId}
                                date={date}
                                subject='Management'
                                isActionRequired={info.isActionRequired}
                                weeklyInfoId={info.id}
                              />
                            )
                          }}
                        </Query>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Paper>
        )
      }}
    </Query>
  )
}

@withUser
class ManagerPage extends React.Component {
  constructor() {
    super()
    this.state = {
      imageOverlayIsOpen: false,
      selectedImage: null,
    }
  }

  selectImage = (imageContent: string) => {
    this.setState({
      selectedImage: imageContent,
      imageOverlayIsOpen: true,
    })
  }

  render() {
    const { imageOverlayIsOpen, selectedImage } = this.state
    const { user } = this.props
    return (
      <WeeklyInfoPage className={css.component} subject='Management'>
        {({ stationId, date, dateFormat, onDateChange, companyId }) => (
          <div className={css.content}>
            <ImageOverlay
              isOpen={imageOverlayIsOpen}
              image={selectedImage}
              onClose={() =>
                this.setState({
                  selectedImage: null,
                  imageOverlayIsOpen: false,
                })
              }
            />

            <SalesAndHours stationId={stationId} date={date} />

            <ScoreCardTable stationId={stationId} date={date} />

            <Images stationId={stationId} selectImage={this.selectImage} user={user} imageType={'weekinfo_manager'} />

            <Links stationId={stationId} linkType={'weekinfo_manager'} companyId={companyId} />
          </div>
        )}
      </WeeklyInfoPage>
    )
  }
}

export default ManagerPage
